/*
 * The fleet's account menu (`tiketo-cms` @ `c4387239`,
 * `frontend-app/src/components/layout`), with one forced change: `useTranslation`
 * is ours, because this console has no i18next. Everything else is the fleet's
 * file, including `VITE_APP_VERSION` — undefined here until something sets it,
 * which draws an empty line rather than a wrong version.
 */
import { ChevronsUpDown, LogOut } from 'lucide-react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SidebarMenuButton, useSidebar } from '@/components/ui/sidebar'
import type { PropsWithChildren } from 'react'
import { useTranslation } from '@/hooks/useTranslation'

export type LayoutUserDropdownProps = PropsWithChildren<{
  user?: {
    name?: string
    email?: string
    avatar?: string
  }
  signOut?: () => void
}>

export const LayoutUserDropdown = ({
  children,
  user,
  signOut,
}: LayoutUserDropdownProps) => {
  const { isMobile } = useSidebar()
  const { t } = useTranslation()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton
          size="lg"
          className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
          <Avatar className="h-8 w-8 rounded-lg">
            <AvatarImage src={user?.avatar} alt={user?.name} />
            <AvatarFallback className="rounded-lg">
              {user?.name?.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="grid flex-1 text-left text-sm leading-tight">
            <span className="truncate font-medium">{user?.name}</span>
            <span className="truncate text-xs">{user?.email}</span>
          </div>
          <ChevronsUpDown className="ml-auto size-4" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
        side={isMobile ? 'bottom' : 'right'}
        align="end"
        sideOffset={4}>
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
            <Avatar className="h-8 w-8 rounded-lg">
              <AvatarImage src={user?.avatar} alt={user?.name} />
              <AvatarFallback className="rounded-lg">
                {user?.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{user?.name}</span>
              <span className="truncate text-xs">{user?.email}</span>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {children}
        {children && <DropdownMenuSeparator />}
        <DropdownMenuItem onClick={signOut} className="cursor-pointer">
          <LogOut />
          {t('sideBar.userNav.logout')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5 text-xs text-muted-foreground text-center">
          {import.meta.env.VITE_APP_VERSION}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
